"use client";

import { useEffect } from "react";

import { AvailabilitySearchParameters } from "@/types/AvailabilitySearchParameters";
import { UnitAvailabilityOption } from "@/types/UnitAvailabilityOption";

type TripteaseAvailabilitySearchTriggerProps = {
  hotelId: string;
  currencyCode: string;
  availabilitySearchParameters: AvailabilitySearchParameters;
  unitAvailabilityOptions: UnitAvailabilityOption[];
};

const cleanUpLinkedData = () => {
  const scripts = [].slice.call(
    document.querySelectorAll("script.triptease-linked-data")
  );

  scripts.forEach((script: Element) => script.remove());
};

const setLinkedData = (data: any[] = []) => {
  cleanUpLinkedData();
  const applicationDetails = {
    "@context": "http://schema.org/",
    "@type": "SoftwareApplication",
    name: "ThinkReservations",
    version: "1.0.0",
  };

  const newData = [applicationDetails].concat(data);
  const script = document.createElement("script");
  script.setAttribute("class", "triptease-linked-data");
  script.type = "application/ld+json";
  script.innerHTML = JSON.stringify(newData, null, 2);
  document.body.appendChild(script);
};

const TripteaseAvailabilitySearchTriggerClient = ({
  hotelId,
  currencyCode,
  availabilitySearchParameters,
  unitAvailabilityOptions,
}: TripteaseAvailabilitySearchTriggerProps) => {
  useEffect(() => {
    console.log("unitAvailabilityOptions", unitAvailabilityOptions);
    const availabilityOptionsData =
      unitAvailabilityOptions.length === 0
        ? [
            {
              "@context": "http://schema.org/",
              "@type": "OfferCatalog",
              numberOfItems: 0,
            },
          ]
        : unitAvailabilityOptions
            .map((unitAvailabilityOption) => {
              const { availabilityOption } = unitAvailabilityOption;
              if (!availabilityOption) {
                return [];
              }

              return availabilityOption.rateTypeAvailabilities.map(
                (rateTypeAvailability) => {
                  return {
                    "@context": "http://schema.org/",
                    "@type": "Offer",
                    itemOffered: {
                      "@type": "HotelRoom",
                      name: availabilityOption.unit.name,
                      identifier: availabilityOption.unit.id,
                    },
                    name: availabilityOption.unit.name,
                    identifier: rateTypeAvailability.rateType.id,
                    priceSpecification: {
                      "@type": "UnitPriceSpecification",
                      price: rateTypeAvailability.averagePricePerDay.toString(),
                      priceCurrency: currencyCode,
                      unitText: "Nightly",
                    },
                  };
                }
              );
            })
            .flat();

    const data = [
      {
        "@context": "https://structured-data.triptease.io",
        "@type": "LodgingSearch",
        checkinTime: `${availabilitySearchParameters.startDate}T00:00:00-00:00`,
        checkoutTime: `${availabilitySearchParameters.endDate}T00:00:00-00:00`,
        numAdults: availabilitySearchParameters.numberOfAdults.toString(),
        numChildren: availabilitySearchParameters.numberOfChildren.toString(),
      },
      ...availabilityOptionsData,
    ];
    setLinkedData(data);
  }, [unitAvailabilityOptions]);

  return null;
};

export default TripteaseAvailabilitySearchTriggerClient;
